<template>
	<div class="font-sans">
		<!-- ── Create button ── -->
		<div v-if="userInfo.is_system_admin" class="flex justify-end mb-5">
			<router-link
				to="/admin/organization"
				class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-bold bg-primary-400 text-white hover:bg-primary-600 hover:-translate-y-0.5 shadow-teal transition-all duration-200">
				<svg
					class="w-4 h-4"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2.5"
						d="M12 4v16m8-8H4" />
				</svg>
				Create Organizer
			</router-link>
		</div>

		<!-- ── Table ── -->
		<div
			v-if="organizerStore.length > 0 && userInfo.is_system_admin"
			class="overflow-x-auto rounded-xl border border-primary-100 dark:border-dark-600">
			<table class="w-full border-separate border-spacing-0">
				<thead>
					<tr>
						<th
							v-for="h in ['User Name', 'Email', 'Phone Number', 'Created At']"
							:key="h"
							class="text-left text-[11px] font-bold uppercase tracking-widest px-4 py-3 text-muted dark:text-muted-light bg-primary-50 dark:bg-dark-600 border-b border-primary-100 dark:border-dark-500">
							{{ h }}
						</th>
						<th
							class="text-center text-[11px] font-bold uppercase tracking-widest px-4 py-3 text-muted dark:text-muted-light bg-primary-100 dark:bg-dark-500 border-b border-primary-100 dark:border-dark-500">
							Action
						</th>
					</tr>
				</thead>
				<tbody>
					<tr
						v-for="user in paginatedOrganizers"
						:key="user.ID"
						class="hover:bg-primary-50 dark:hover:bg-dark-600/50 transition-colors duration-150">
						<td
							class="px-4 py-3.5 text-[13px] font-semibold text-dark-700 dark:text-white border-b border-primary-50 dark:border-dark-600 whitespace-nowrap">
							{{ user.Name }}
						</td>
						<td
							class="px-4 py-3.5 text-[13px] text-muted dark:text-muted-light border-b border-primary-50 dark:border-dark-600 whitespace-nowrap">
							{{ user.Email }}
						</td>
						<td
							class="px-4 py-3.5 text-[13px] text-muted dark:text-muted-light border-b border-primary-50 dark:border-dark-600 whitespace-nowrap">
							{{ user.Phone }}
						</td>
						<td
							class="px-4 py-3.5 text-[13px] text-muted dark:text-muted-light border-b border-primary-50 dark:border-dark-600 whitespace-nowrap">
							{{ $formatDate(user.CreatedAt) }}
						</td>
						<td
							class="px-4 py-3.5 text-center border-b border-primary-50 dark:border-dark-600">
							<span
								v-if="user.is_current_user"
								class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-primary-50 dark:bg-primary-400/15 text-primary-600 dark:text-primary-300">
								<span class="w-1.5 h-1.5 rounded-full bg-primary-400" />
								You
							</span>
							<button
								v-if="!user.is_current_user && !user.delete"
								@click="deleteOrganizer(user)"
								:disabled="loadingUserId === user.ID"
								class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[12px] font-semibold bg-red-50 dark:bg-red-950/30 text-red-500 border border-red-200 dark:border-red-800/50 hover:bg-red-100 dark:hover:bg-red-950/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150">
								<svg
									v-if="loadingUserId === user.ID"
									class="animate-spin w-3.5 h-3.5"
									fill="none"
									viewBox="0 0 24 24">
									<circle
										class="opacity-25"
										cx="12"
										cy="12"
										r="10"
										stroke="currentColor"
										stroke-width="4" />
									<path
										class="opacity-75"
										fill="currentColor"
										d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
								</svg>
								Delete
							</button>
						</td>
					</tr>
				</tbody>
			</table>
		</div>

		<!-- ── Permission denied ── -->
		<div
			v-else-if="!userInfo.is_system_admin"
			class="flex flex-col items-center justify-center py-16 gap-3 rounded-card border border-primary-100 dark:border-dark-600">
			<div
				class="w-12 h-12 rounded-2xl bg-red-50 dark:bg-red-950/30 flex items-center justify-center">
				<svg
					class="w-6 h-6 text-red-400"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
				</svg>
			</div>
			<p
				class="font-display font-bold text-[14px] text-dark-700 dark:text-white">
				Access Restricted
			</p>
			<p class="text-[13px] text-muted dark:text-muted-light">
				You do not have permission to view organizers.
			</p>
		</div>

		<!-- ── Empty ── -->
		<div
			v-else
			class="flex flex-col items-center justify-center py-16 gap-3 rounded-card border border-primary-100 dark:border-dark-600">
			<div
				class="w-12 h-12 rounded-2xl bg-primary-50 dark:bg-dark-600 flex items-center justify-center">
				<svg
					class="w-6 h-6 text-muted dark:text-muted-light"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5" />
				</svg>
			</div>
			<p
				class="font-display font-bold text-[14px] text-dark-700 dark:text-white">
				No organizers found
			</p>
		</div>

		<!-- ── Pagination ── -->
		<div
			v-if="totalPages > 1"
			class="flex items-center justify-between mt-5 px-1">
			<button
				@click="prevPage"
				:disabled="currentPage === 1"
				class="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-[13px] font-semibold bg-primary-50 dark:bg-dark-600 border border-primary-100 dark:border-dark-500 text-dark-400 dark:text-white/70 hover:bg-primary-100 dark:hover:bg-dark-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150">
				<svg
					class="w-4 h-4"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M15 19l-7-7 7-7" />
				</svg>
				Previous
			</button>

			<span class="text-[13px] font-semibold text-muted dark:text-muted-light">
				Page
				<span class="text-dark-700 dark:text-white">{{ currentPage }}</span> of
				<span class="text-dark-700 dark:text-white">{{ totalPages }}</span>
			</span>

			<button
				@click="nextPage"
				:disabled="currentPage === totalPages"
				class="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-[13px] font-semibold bg-primary-50 dark:bg-dark-600 border border-primary-100 dark:border-dark-500 text-dark-400 dark:text-white/70 hover:bg-primary-100 dark:hover:bg-dark-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150">
				Next
				<svg
					class="w-4 h-4"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M9 5l7 7-7 7" />
				</svg>
			</button>
		</div>
	</div>
</template>

<script>
import { ref, watch, computed } from "vue";
import { apiDataService } from "@/services/apiDataService";
import { showToast } from "@/constants/toast";
import { useRouter } from "vue-router";

export default {
	name: "OrganizersList",
	props: { organizers: { type: Array, default: () => [] } },
	computed: {
		userInfo() {
			return this.$store.state.auth.user || {};
		},
	},
	setup(props) {
		const organizerStore = ref(props.organizers);
		const loadingUserId = ref(null);
		const router = useRouter();
		const currentPage = ref(1);
		const pageSize = ref(10);

		watch(
			() => props.organizers,
			(newOrganizers) => {
				organizerStore.value = newOrganizers;
				currentPage.value = 1;
			},
			{ immediate: true },
		);

		const totalPages = computed(() =>
			Math.ceil(organizerStore.value.length / pageSize.value),
		);
		const paginatedOrganizers = computed(() => {
			const start = (currentPage.value - 1) * pageSize.value;
			return organizerStore.value.slice(start, start + pageSize.value);
		});

		const nextPage = () => {
			if (currentPage.value < totalPages.value) currentPage.value++;
		};
		const prevPage = () => {
			if (currentPage.value > 1) currentPage.value--;
		};

		const deleteOrganizer = async (organizer) => {
			loadingUserId.value = organizer.ID;
			try {
				const response = await apiDataService.deteleOrganizer(organizer.ID, 1);
				showToast("success", "Organization deleted successfully");
				console.log("Organizer deleted:", response);
				router.push("/admin/organization/all");
			} catch (error) {
				console.error("User status update failed:", error);
			} finally {
				loadingUserId.value = null;
			}
		};

		return {
			organizerStore,
			deleteOrganizer,
			paginatedOrganizers,
			currentPage,
			totalPages,
			nextPage,
			prevPage,
		};
	},
};
</script>
